import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import Button from '~/app/components/Button';
import { COMPONENT_VALUE_TYPES } from 'shared/constants';
import { isDateTimePropSchema } from 'shared/socialIconEnumOptions';
import DateTimePicker from '~/pages/components/PageEditor/DateTimePicker';
import { S, A, TONES, DARK } from '~/app/ui/tokens';

const useStyles = createUseStyles({
  '@keyframes highlightMove': {
    '0%': {
      backgroundColor: '#dbeafe',
      transform: 'scale(1.01)',
      boxShadow: '0 0 0 2px #3b82f6',
    },
    '100%': {
      backgroundColor: '#f8fafc',
      transform: 'scale(1)',
      boxShadow: 'none',
    },
  },
  '@keyframes highlightMoveDark': {
    '0%': {
      backgroundColor: A.rowSelected,
      transform: 'scale(1.01)',
      boxShadow: `0 0 0 2px ${S.blue}`,
    },
    '100%': {
      backgroundColor: S.chrome,
      transform: 'scale(1)',
      boxShadow: 'none',
    },
  },
  container: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
  },
  empty: {
    textAlign: 'center',
    padding: '24px',
    color: '#94a3b8',
    fontSize: '14px',
    [DARK]: { color: S.muted },
  },
  propItem: {
    background: '#f8fafc',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '12px',
    border: '1px solid #e2e8f0',
    transition: 'all 0.15s ease-out',
    [DARK]: { background: S.chrome, borderColor: S.line },
  },
  propItemHighlight: {
    animation: '$highlightMove 0.4s ease-out',
    [DARK]: { animation: '$highlightMoveDark 0.4s ease-out' },
  },
  propItemNested: {
    background: A.surface,
    borderLeft: '3px solid #3b82f6',
    [DARK]: { background: A.surface, borderLeftColor: S.blue },
  },
  propHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  propKey: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#0f172a',
    fontFamily: 'monospace',
    [DARK]: { color: S.fg },
  },
  deleteButton: {
    background: 'transparent',
    border: 'none',
    color: '#ef4444',
    cursor: 'pointer',
    padding: '4px',
    fontSize: '18px',
    lineHeight: 1,
    '&:hover': {
      background: '#fee2e2',
      borderRadius: '4px',
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    [DARK]: { color: S.red, '&:hover': { background: TONES.red.bg } },
  },
  reorderButtons: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: '8px',
  },
  reorderButton: {
    background: 'transparent',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    padding: '2px 4px',
    fontSize: '10px',
    lineHeight: 1,
    '&:hover': {
      background: '#e2e8f0',
      borderRadius: '2px',
      color: '#0f172a',
    },
    '&:disabled': {
      opacity: 0.3,
      cursor: 'not-allowed',
      '&:hover': {
        background: 'transparent',
        color: '#64748b',
      },
    },
    [DARK]: {
      color: S.muted,
      '&:hover': { background: S.chromeHover, color: S.fg },
      '&:disabled:hover': { background: 'transparent', color: S.muted },
    },
  },
  field: {
    marginBottom: '10px',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 500,
    color: '#64748b',
    marginBottom: '4px',
    [DARK]: { color: S.muted },
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    fontSize: '13px',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    '&:focus': {
      outline: 'none',
      borderColor: '#3b82f6',
    },
    '&:disabled': {
      background: '#f1f5f9',
      cursor: 'not-allowed',
    },
    [DARK]: {
      background: S.field,
      color: S.fg,
      borderColor: S.fieldLine,
      '&:focus': { borderColor: S.blue },
      '&:disabled': { background: S.chrome },
    },
  },
  select: {
    width: '100%',
    padding: '8px 10px',
    fontSize: '13px',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    background: S.field,
    cursor: 'pointer',
    boxSizing: 'border-box',
    '&:focus': {
      outline: 'none',
      borderColor: '#3b82f6',
    },
    '&:disabled': {
      background: '#f1f5f9',
      cursor: 'not-allowed',
    },
    [DARK]: {
      color: S.fg,
      borderColor: S.fieldLine,
      '&:focus': { borderColor: S.blue },
      '&:disabled': { background: S.chrome },
    },
  },
  addButton: {
    width: '100%',
    marginTop: '8px',
  },
  hint: {
    fontSize: '11px',
    color: '#94a3b8',
    marginTop: '4px',
    [DARK]: { color: S.muted },
  },
  // Faint helper text (was inline)
  faint: {
    color: '#94a3b8',
    [DARK]: { color: S.muted },
  },
  sectionTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '12px',
    [DARK]: { color: S.muted },
  },
  childrenSection: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px dashed #e2e8f0',
    [DARK]: { borderTopColor: S.line },
  },
  childrenHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  childrenTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#3b82f6',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    [DARK]: { color: S.blue },
  },
  childrenList: {
    marginLeft: '8px',
    paddingLeft: '8px',
    borderLeft: '2px solid #e2e8f0',
    [DARK]: { borderLeftColor: S.line },
  },
  addChildButton: {
    fontSize: '12px',
    padding: '4px 8px',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '4px',
    color: '#2563eb',
    cursor: 'pointer',
    '&:hover': {
      background: '#dbeafe',
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    [DARK]: {
      background: A.rowSelected,
      borderColor: TONES.blue.bg,
      color: TONES.blue.fg,
      '&:hover': { background: TONES.blue.bg },
    },
  },
  typeTag: {
    fontSize: '10px',
    padding: '2px 6px',
    borderRadius: '4px',
    fontWeight: 600,
    textTransform: 'uppercase',
    marginLeft: '8px',
  },
  typeTagArray: {
    background: '#fef3c7',
    color: '#92400e',
    [DARK]: { background: TONES.amber.bg, color: TONES.amber.fg },
  },
  typeTagObject: {
    background: '#e0e7ff',
    color: '#3730a3',
    [DARK]: { background: TONES.blue.bg, color: TONES.blue.fg },
  },
  collapseButton: {
    background: 'transparent',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '2px 6px',
    borderRadius: '4px',
    marginRight: '8px',
    '&:hover': {
      background: '#f1f5f9',
    },
    [DARK]: { color: S.muted, '&:hover': { background: S.chromeHover } },
  },
  defaultItemsSection: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px dashed #e2e8f0',
    [DARK]: { borderTopColor: S.line },
  },
  defaultItemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  defaultItemTitle: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#64748b',
    [DARK]: { color: S.muted },
  },
  defaultItem: {
    background: '#fefce8',
    borderRadius: '6px',
    padding: '10px',
    marginBottom: '8px',
    border: '1px solid #fef08a',
    [DARK]: { background: TONES.amber.bg, borderColor: TONES.amber.bg },
  },
  defaultItemIndex: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#a16207',
    marginBottom: '8px',
    [DARK]: { color: TONES.amber.fg },
  },
  removeItemButton: {
    fontSize: '11px',
    padding: '2px 6px',
    background: '#fee2e2',
    border: '1px solid #fecaca',
    borderRadius: '4px',
    color: '#dc2626',
    cursor: 'pointer',
    '&:hover': {
      background: '#fecaca',
    },
    [DARK]: {
      background: TONES.red.bg,
      borderColor: TONES.red.bg,
      color: TONES.red.fg,
      '&:hover': { borderColor: TONES.red.fg },
    },
  },
  // Nested array editor styles
  nestedArrayContainer: {
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    padding: '8px',
    background: '#fafafa',
    [DARK]: { borderColor: S.line, background: S.chrome },
  },
  nestedArrayItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '6px',
  },
  nestedArrayInput: {
    flex: 1,
    padding: '6px 8px',
    fontSize: '12px',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    '&:focus': {
      outline: 'none',
      borderColor: '#3b82f6',
    },
    [DARK]: {
      background: S.field,
      color: S.fg,
      borderColor: S.fieldLine,
      '&:focus': { borderColor: S.blue },
    },
  },
  nestedArrayRemoveBtn: {
    padding: '4px 8px',
    fontSize: '11px',
    background: '#fee2e2',
    border: '1px solid #fecaca',
    borderRadius: '4px',
    color: '#dc2626',
    cursor: 'pointer',
    '&:hover': {
      background: '#fecaca',
    },
    [DARK]: {
      background: TONES.red.bg,
      borderColor: TONES.red.bg,
      color: TONES.red.fg,
      '&:hover': { borderColor: TONES.red.fg },
    },
  },
  nestedArrayAddBtn: {
    padding: '4px 10px',
    fontSize: '11px',
    background: '#dbeafe',
    border: '1px solid #93c5fd',
    borderRadius: '4px',
    color: '#1d4ed8',
    cursor: 'pointer',
    '&:hover': {
      background: '#bfdbfe',
    },
    [DARK]: {
      background: A.rowSelected,
      borderColor: TONES.blue.bg,
      color: TONES.blue.fg,
      '&:hover': { background: TONES.blue.bg },
    },
  },
  nestedArrayEmpty: {
    fontSize: '11px',
    color: '#94a3b8',
    padding: '4px 0',
    [DARK]: { color: S.muted },
  },
  // Array syntax help (was inline)
  syntaxBox: {
    background: '#f8fafc',
    color: '#64748b',
    [DARK]: { background: S.chrome, color: S.muted },
  },
  syntaxCode: {
    background: '#e2e8f0',
    [DARK]: { background: S.chromeHover },
  },
});

interface EnumOption {
  label: string;
  value: string;
}

interface PropsSchemaItem {
  key: string;
  label: string;
  type: string;
  defaultValue?: any;
  options?: EnumOption[];
  children?: PropsSchemaItem[];
}

interface Props {
  schema: PropsSchemaItem[];
  onChange: (schema: PropsSchemaItem[]) => void;
  disabled?: boolean;
}

// Available prop types
const PROP_TYPES = [
  { value: COMPONENT_VALUE_TYPES.TEXT, label: 'Text' },
  { value: COMPONENT_VALUE_TYPES.TEXTAREA, label: 'Textarea' },
  { value: COMPONENT_VALUE_TYPES.BOOLEAN, label: 'Boolean' },
  { value: COMPONENT_VALUE_TYPES.S3_ASSET_URL, label: 'Image URL' },
  { value: COMPONENT_VALUE_TYPES.COLOR_PICKER, label: 'Color' },
  { value: COMPONENT_VALUE_TYPES.HTML, label: 'HTML' },
  { value: COMPONENT_VALUE_TYPES.RICHTEXT, label: 'Rich Text' },
  { value: COMPONENT_VALUE_TYPES.TINYMCE, label: 'TinyMCE' },
  { value: COMPONENT_VALUE_TYPES.ENUM, label: 'Select (dropdown)' },
  { value: COMPONENT_VALUE_TYPES.DATE, label: 'Date' },
  { value: COMPONENT_VALUE_TYPES.DATETIME, label: 'Date & time' },
  { value: COMPONENT_VALUE_TYPES.ARRAY, label: 'Array (List)' },
  { value: COMPONENT_VALUE_TYPES.OBJECT, label: 'Object (Group)' },
  { value: COMPONENT_VALUE_TYPES.LIVESTREAM_CONTROL, label: 'Livestream control' },
  { value: COMPONENT_VALUE_TYPES.INFO, label: 'Info / How-to (read-only)' },
];

// Get default value for a type
const getDefaultValueForType = (type: string): any => {
  switch (type) {
    case COMPONENT_VALUE_TYPES.BOOLEAN:
      return false;
    case COMPONENT_VALUE_TYPES.ARRAY:
      return [];
    case COMPONENT_VALUE_TYPES.OBJECT:
      return {};
    case COMPONENT_VALUE_TYPES.COLOR_PICKER:
      return '#000000';
    case COMPONENT_VALUE_TYPES.DATE:
    case COMPONENT_VALUE_TYPES.DATETIME:
      return {};
    case COMPONENT_VALUE_TYPES.LIVESTREAM_CONTROL:
      return { path: '', controlUrl: '', pollSeconds: 15, scenes: [], activeId: '' };
    default:
      return '';
  }
};

// Build default object from children schema
const buildDefaultFromSchema = (children: PropsSchemaItem[] = []): Record<string, any> => {
  const obj: Record<string, any> = {};
  children.forEach(child => {
    if (child.type === COMPONENT_VALUE_TYPES.ARRAY) {
      obj[child.key] = child.defaultValue || [];
    } else if (child.type === COMPONENT_VALUE_TYPES.OBJECT) {
      obj[child.key] = child.defaultValue || buildDefaultFromSchema(child.children);
    } else {
      obj[child.key] = child.defaultValue ?? getDefaultValueForType(child.type);
    }
  });
  return obj;
};

// Nested array editor for simple string arrays (like features list)
interface NestedArrayEditorProps {
  values: string[];
  onChange: (values: string[]) => void;
  disabled: boolean;
  placeholder?: string;
}

const NestedArrayEditor: React.FC<NestedArrayEditorProps> = ({
  values,
  onChange,
  disabled,
  placeholder = 'Add item',
}) => {
  const c = useStyles();

  const handleAdd = () => {
    onChange([...values, '']);
  };

  const handleUpdate = (index: number, value: string) => {
    const newValues = [...values];
    newValues[index] = value;
    onChange(newValues);
  };

  const handleRemove = (index: number) => {
    const newValues = values.filter((_, i) => i !== index);
    onChange(newValues);
  };

  return (
    <div className={c.nestedArrayContainer}>
      {values.length === 0 ? (
        <div className={c.nestedArrayEmpty}>No items yet</div>
      ) : (
        values.map((value, index) => (
          <div key={index} className={c.nestedArrayItem}>
            <input
              type="text"
              className={c.nestedArrayInput}
              value={value}
              onChange={(e) => handleUpdate(index, e.target.value)}
              placeholder={`Item ${index + 1}`}
              disabled={disabled}
            />
            <button
              className={c.nestedArrayRemoveBtn}
              onClick={() => handleRemove(index)}
              disabled={disabled}
              type="button"
            >
              ×
            </button>
          </div>
        ))
      )}
      <button
        className={c.nestedArrayAddBtn}
        onClick={handleAdd}
        disabled={disabled}
        type="button"
      >
        + {placeholder}
      </button>
    </div>
  );
};

// Editor for a Select (dropdown) prop's options — each option is a {label, value} pair
interface EnumOptionsEditorProps {
  options: EnumOption[];
  onChange: (options: EnumOption[]) => void;
  disabled: boolean;
}

const EnumOptionsEditor: React.FC<EnumOptionsEditorProps> = ({ options, onChange, disabled }) => {
  const c = useStyles();
  const list = options || [];

  const handleAdd = () => onChange([...list, { label: '', value: '' }]);
  const handleUpdate = (index: number, field: keyof EnumOption, value: string) => {
    onChange(list.map((o, i) => (i === index ? { ...o, [field]: value } : o)));
  };
  const handleRemove = (index: number) => onChange(list.filter((_, i) => i !== index));

  return (
    <div className={c.nestedArrayContainer}>
      {list.length === 0 ? (
        <div className={c.nestedArrayEmpty}>No options yet</div>
      ) : (
        list.map((opt, index) => (
          <div key={index} className={c.nestedArrayItem}>
            <input
              type="text"
              className={c.nestedArrayInput}
              value={opt.label}
              onChange={(e) => handleUpdate(index, 'label', e.target.value)}
              placeholder="Label (e.g. Dark)"
              disabled={disabled}
            />
            <input
              type="text"
              className={c.nestedArrayInput}
              value={opt.value}
              onChange={(e) => handleUpdate(index, 'value', e.target.value)}
              placeholder="Value (e.g. dark)"
              disabled={disabled}
            />
            <button
              className={c.nestedArrayRemoveBtn}
              onClick={() => handleRemove(index)}
              disabled={disabled}
              type="button"
            >
              ×
            </button>
          </div>
        ))
      )}
      <button
        className={c.nestedArrayAddBtn}
        onClick={handleAdd}
        disabled={disabled}
        type="button"
      >
        + Add option
      </button>
    </div>
  );
};

interface PropItemEditorProps {
  prop: PropsSchemaItem;
  index: number;
  depth: number;
  disabled: boolean;
  isFirst: boolean;
  isLast: boolean;
  isHighlighted: boolean;
  onUpdate: (field: keyof PropsSchemaItem, value: any) => void;
  onDelete: () => void;
  onUpdateChildren: (children: PropsSchemaItem[]) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

const PropItemEditor: React.FC<PropItemEditorProps> = ({
  prop,
  index,
  depth,
  disabled,
  isFirst,
  isLast,
  isHighlighted,
  onUpdate,
  onDelete,
  onUpdateChildren,
  onMoveUp,
  onMoveDown,
}) => {
  const c = useStyles();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isChildrenExpanded, setIsChildrenExpanded] = useState(true);
  const [highlightedChildIndex, setHighlightedChildIndex] = useState<number | null>(null);

  const isArray = prop.type === COMPONENT_VALUE_TYPES.ARRAY;
  const isObject = prop.type === COMPONENT_VALUE_TYPES.OBJECT;
  const isEnum = prop.type === COMPONENT_VALUE_TYPES.ENUM;
  const hasChildren = isArray || isObject;

  const handleAddChild = () => {
    const children = prop.children || [];
    const newChild: PropsSchemaItem = {
      key: `field${children.length + 1}`,
      label: `Field ${children.length + 1}`,
      type: COMPONENT_VALUE_TYPES.TEXT,
      defaultValue: '',
    };
    onUpdateChildren([...children, newChild]);
  };

  const handleUpdateChild = (childIndex: number, field: keyof PropsSchemaItem, value: any) => {
    const children = [...(prop.children || [])];
    children[childIndex] = { ...children[childIndex], [field]: value };
    onUpdateChildren(children);
  };

  const handleDeleteChild = (childIndex: number) => {
    const children = (prop.children || []).filter((_, i) => i !== childIndex);
    onUpdateChildren(children);
  };

  const handleUpdateChildChildren = (childIndex: number, childChildren: PropsSchemaItem[]) => {
    const children = [...(prop.children || [])];
    children[childIndex] = { ...children[childIndex], children: childChildren };
    onUpdateChildren(children);
  };

  const handleMoveChild = (fromIndex: number, toIndex: number) => {
    const children = prop.children || [];
    if (toIndex < 0 || toIndex >= children.length) return;
    const updated = [...children];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    onUpdateChildren(updated);
    // Highlight the moved child
    setHighlightedChildIndex(toIndex);
    setTimeout(() => setHighlightedChildIndex(null), 400);
  };

  // Handle default array items
  const defaultItems = isArray ? (prop.defaultValue as any[] || []) : [];

  const handleAddDefaultItem = () => {
    const newItem = buildDefaultFromSchema(prop.children);
    onUpdate('defaultValue', [...defaultItems, newItem]);
  };

  const handleRemoveDefaultItem = (itemIndex: number) => {
    const newItems = defaultItems.filter((_, i) => i !== itemIndex);
    onUpdate('defaultValue', newItems);
  };

  const handleUpdateDefaultItem = (itemIndex: number, fieldKey: string, value: any) => {
    const newItems = [...defaultItems];
    newItems[itemIndex] = { ...newItems[itemIndex], [fieldKey]: value };
    onUpdate('defaultValue', newItems);
  };

  const getDefaultValuePlaceholder = (type: string) => {
    switch (type) {
      case COMPONENT_VALUE_TYPES.BOOLEAN:
        return 'true or false';
      case COMPONENT_VALUE_TYPES.S3_ASSET_URL:
        return 'https://...';
      case COMPONENT_VALUE_TYPES.COLOR_PICKER:
        return '#000000';
      default:
        return 'Default value';
    }
  };

  const itemClassName = `${c.propItem} ${depth > 0 ? c.propItemNested : ''} ${isHighlighted ? c.propItemHighlight : ''}`;

  return (
    <div className={itemClassName}>
      <div className={c.propHeader}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {/* Reorder buttons */}
          <div className={c.reorderButtons}>
            <button
              className={c.reorderButton}
              onClick={onMoveUp}
              disabled={disabled || isFirst}
              title="Move up"
              type="button"
            >
              ▲
            </button>
            <button
              className={c.reorderButton}
              onClick={onMoveDown}
              disabled={disabled || isLast}
              title="Move down"
              type="button"
            >
              ▼
            </button>
          </div>
          {hasChildren && (
            <button
              className={c.collapseButton}
              onClick={() => setIsExpanded(!isExpanded)}
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          )}
          <span className={c.propKey}>
            {isArray ? `{{#${prop.key}}}...{{/${prop.key}}}` : `{{${prop.key}}}`}
          </span>
          {isArray && <span className={`${c.typeTag} ${c.typeTagArray}`}>Array</span>}
          {isObject && <span className={`${c.typeTag} ${c.typeTagObject}`}>Object</span>}
        </div>
        <button
          className={c.deleteButton}
          onClick={onDelete}
          disabled={disabled}
          title="Delete prop"
        >
          ×
        </button>
      </div>

      {isExpanded && (
        <>
          <div className={c.field}>
            <label className={c.label}>Key</label>
            <input
              type="text"
              className={c.input}
              value={prop.key}
              onChange={(e) => onUpdate('key', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
              placeholder="propName"
              disabled={disabled}
            />
            <div className={c.hint}>
              {isArray 
                ? `Loop: {{#${prop.key || 'items'}}}...{{/${prop.key || 'items'}}}` 
                : `Use in HTML as {{${prop.key || 'propName'}}}`
              }
            </div>
          </div>

          <div className={c.field}>
            <label className={c.label}>Label</label>
            <input
              type="text"
              className={c.input}
              value={prop.label}
              onChange={(e) => onUpdate('label', e.target.value)}
              placeholder="Display Label"
              disabled={disabled}
            />
          </div>

          <div className={c.field}>
            <label className={c.label}>Type</label>
            <select
              className={c.select}
              value={prop.type || COMPONENT_VALUE_TYPES.TEXT}
              onChange={(e) => {
                const newType = e.target.value;
                // Update type - this will trigger a re-render
                onUpdate('type', newType);
              }}
              disabled={disabled}
            >
              {PROP_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Options editor for Select (dropdown) props */}
          {isEnum && (
            <div className={c.field}>
              <label className={c.label}>Dropdown options</label>
              <EnumOptionsEditor
                options={prop.options || []}
                onChange={(opts) => onUpdate('options', opts)}
                disabled={disabled}
              />
              <div className={c.hint}>
                Each option has a Label (shown in the dropdown) and a Value (inserted by {`{{${prop.key || 'propName'}}}`}).
              </div>
            </div>
          )}

          {/* Default value for simple types */}
          {!hasChildren && (
            <div className={c.field}>
              <label className={c.label}>Default Value</label>
              {isEnum ? (
                <select
                  className={c.select}
                  value={prop.defaultValue || ''}
                  onChange={(e) => onUpdate('defaultValue', e.target.value)}
                  disabled={disabled}
                >
                  <option value="">— Select —</option>
                  {(prop.options || []).map((opt, i) => (
                    <option key={i} value={opt.value}>{opt.label || opt.value}</option>
                  ))}
                </select>
              ) : prop.type === COMPONENT_VALUE_TYPES.BOOLEAN ? (
                <select
                  className={c.select}
                  value={String(prop.defaultValue === true || prop.defaultValue === 'true')}
                  onChange={(e) => onUpdate('defaultValue', e.target.value === 'true')}
                  disabled={disabled}
                >
                  <option value="false">False</option>
                  <option value="true">True</option>
                </select>
              ) : prop.type === COMPONENT_VALUE_TYPES.TEXTAREA || prop.type === COMPONENT_VALUE_TYPES.HTML || prop.type === COMPONENT_VALUE_TYPES.RICHTEXT || prop.type === COMPONENT_VALUE_TYPES.TINYMCE ? (
                <textarea
                  className={c.input}
                  value={prop.defaultValue || ''}
                  onChange={(e) => onUpdate('defaultValue', e.target.value)}
                  placeholder={getDefaultValuePlaceholder(prop.type)}
                  rows={3}
                  disabled={disabled}
                  style={{ resize: 'vertical' }}
                />
              ) : isDateTimePropSchema(prop) ? (
                <DateTimePicker
                  value={prop.defaultValue && typeof prop.defaultValue === 'object' ? prop.defaultValue : {}}
                  onChange={(newVal) => onUpdate('defaultValue', newVal)}
                />
              ) : (
                <input
                  type={prop.type === COMPONENT_VALUE_TYPES.COLOR_PICKER ? 'color' : 'text'}
                  className={c.input}
                  value={prop.defaultValue || ''}
                  onChange={(e) => onUpdate('defaultValue', e.target.value)}
                  placeholder={getDefaultValuePlaceholder(prop.type)}
                  disabled={disabled}
                />
              )}
            </div>
          )}

          {/* Children section for ARRAY and OBJECT */}
          {hasChildren && (
            <div className={c.childrenSection}>
              <div className={c.childrenHeader}>
                <div className={c.childrenTitle}>
                  <button
                    className={c.collapseButton}
                    onClick={() => setIsChildrenExpanded(!isChildrenExpanded)}
                  >
                    {isChildrenExpanded ? '▼' : '▶'}
                  </button>
                  {isArray ? 'Item Fields' : 'Object Fields'}
                  <span className={c.faint} style={{ fontWeight: 400, fontSize: '11px' }}>
                    ({(prop.children || []).length} fields)
                  </span>
                </div>
                <button
                  className={c.addChildButton}
                  onClick={handleAddChild}
                  disabled={disabled}
                >
                  + Add Field
                </button>
              </div>

              {isChildrenExpanded && (
                <div className={c.childrenList}>
                  {(prop.children || []).length === 0 ? (
                    <div className={c.faint} style={{ fontSize: '12px', padding: '8px 0' }}>
                      No fields defined. Add fields to define the structure.
                    </div>
                  ) : (
                    (prop.children || []).map((child, childIndex) => (
                      <PropItemEditor
                        key={childIndex}
                        prop={child}
                        index={childIndex}
                        depth={depth + 1}
                        disabled={disabled}
                        isFirst={childIndex === 0}
                        isLast={childIndex === (prop.children || []).length - 1}
                        isHighlighted={highlightedChildIndex === childIndex}
                        onUpdate={(field, value) => handleUpdateChild(childIndex, field, value)}
                        onDelete={() => handleDeleteChild(childIndex)}
                        onUpdateChildren={(childChildren) => handleUpdateChildChildren(childIndex, childChildren)}
                        onMoveUp={() => handleMoveChild(childIndex, childIndex - 1)}
                        onMoveDown={() => handleMoveChild(childIndex, childIndex + 1)}
                      />
                    ))
                  )}
                </div>
              )}

              {/* Default items for ARRAY type */}
              {isArray && (prop.children || []).length > 0 && (
                <div className={c.defaultItemsSection}>
                  <div className={c.defaultItemHeader}>
                    <span className={c.defaultItemTitle}>
                      Default Items ({defaultItems.length})
                    </span>
                    <button
                      className={c.addChildButton}
                      onClick={handleAddDefaultItem}
                      disabled={disabled}
                    >
                      + Add Item
                    </button>
                  </div>

                  {defaultItems.map((item, itemIndex) => (
                    <div key={itemIndex} className={c.defaultItem}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span className={c.defaultItemIndex}>Item {itemIndex + 1}</span>
                        <button
                          className={c.removeItemButton}
                          onClick={() => handleRemoveDefaultItem(itemIndex)}
                          disabled={disabled}
                        >
                          Remove
                        </button>
                      </div>
                      {(prop.children || []).map((childSchema) => (
                        <div key={childSchema.key} className={c.field} style={{ marginBottom: '6px' }}>
                          <label className={c.label}>{childSchema.label}</label>
                          {childSchema.type === COMPONENT_VALUE_TYPES.ENUM ? (
                            <select
                              className={c.select}
                              value={item[childSchema.key] || ''}
                              onChange={(e) => handleUpdateDefaultItem(itemIndex, childSchema.key, e.target.value)}
                              disabled={disabled}
                            >
                              <option value="">— Select —</option>
                              {(childSchema.options || []).map((opt, i) => (
                                <option key={i} value={opt.value}>{opt.label || opt.value}</option>
                              ))}
                            </select>
                          ) : childSchema.type === COMPONENT_VALUE_TYPES.BOOLEAN ? (
                            <select
                              className={c.select}
                              value={String(item[childSchema.key] === true)}
                              onChange={(e) => handleUpdateDefaultItem(itemIndex, childSchema.key, e.target.value === 'true')}
                              disabled={disabled}
                            >
                              <option value="false">False</option>
                              <option value="true">True</option>
                            </select>
                          ) : childSchema.type === COMPONENT_VALUE_TYPES.TEXTAREA ? (
                            <textarea
                              className={c.input}
                              value={item[childSchema.key] || ''}
                              onChange={(e) => handleUpdateDefaultItem(itemIndex, childSchema.key, e.target.value)}
                              rows={2}
                              disabled={disabled}
                              style={{ resize: 'vertical' }}
                            />
                          ) : childSchema.type === COMPONENT_VALUE_TYPES.ARRAY ? (
                            <NestedArrayEditor
                              values={item[childSchema.key] || []}
                              onChange={(newArray) => handleUpdateDefaultItem(itemIndex, childSchema.key, newArray)}
                              disabled={disabled}
                              placeholder={`Add ${childSchema.label.toLowerCase()}`}
                            />
                          ) : isDateTimePropSchema(childSchema) ? (
                            <DateTimePicker
                              value={item[childSchema.key] && typeof item[childSchema.key] === 'object' ? item[childSchema.key] : {}}
                              onChange={(newVal) => handleUpdateDefaultItem(itemIndex, childSchema.key, newVal)}
                            />
                          ) : (
                            <input
                              type={childSchema.type === COMPONENT_VALUE_TYPES.COLOR_PICKER ? 'color' : 'text'}
                              className={c.input}
                              value={item[childSchema.key] || ''}
                              onChange={(e) => handleUpdateDefaultItem(itemIndex, childSchema.key, e.target.value)}
                              disabled={disabled}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  ))}

                  {defaultItems.length === 0 && (
                    <div className={c.faint} style={{ fontSize: '12px', padding: '8px 0' }}>
                      No default items. Users will start with an empty list.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const PropsSchemaEditor: React.FC<Props> = ({ schema, onChange, disabled = false }) => {
  const c = useStyles();
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);

  const handleAddProp = () => {
    const newKey = `prop${schema.length + 1}`;
    const newProp: PropsSchemaItem = {
      key: newKey,
      label: `Prop ${schema.length + 1}`,
      type: COMPONENT_VALUE_TYPES.TEXT,
      defaultValue: '',
    };
    onChange([...schema, newProp]);
  };

  const handleUpdateProp = (index: number, field: keyof PropsSchemaItem, value: any) => {
    const updated = [...schema];
    const currentProp = updated[index];
    
    // If type is changing, handle side effects
    if (field === 'type' && value !== currentProp.type) {
      const newType = value;
      const isNewTypeComplex = newType === COMPONENT_VALUE_TYPES.ARRAY || newType === COMPONENT_VALUE_TYPES.OBJECT;
      
      const isNewTypeEnum = newType === COMPONENT_VALUE_TYPES.ENUM;
      updated[index] = {
        ...currentProp,
        type: newType,
        defaultValue: getDefaultValueForType(newType),
        // Clear children if going from complex to simple type
        children: isNewTypeComplex ? (currentProp.children || []) : undefined,
        // Seed one option when becoming a Select; drop options otherwise
        options: isNewTypeEnum
          ? (currentProp.options && currentProp.options.length ? currentProp.options : [{ label: 'Option 1', value: 'option1' }])
          : undefined,
      };
    } else {
      updated[index] = { ...currentProp, [field]: value };
    }
    
    onChange(updated);
  };

  const handleDeleteProp = (index: number) => {
    const updated = schema.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleUpdatePropChildren = (index: number, children: PropsSchemaItem[]) => {
    const updated = [...schema];
    updated[index] = { ...updated[index], children };
    onChange(updated);
  };

  const handleMoveProp = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= schema.length) return;
    const updated = [...schema];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    onChange(updated);
    // Highlight the moved prop
    setHighlightedIndex(toIndex);
    setTimeout(() => setHighlightedIndex(null), 400);
  };

  return (
    <div className={c.container}>
      <div className={c.sectionTitle}>
        Widget Props
      </div>
      
      {schema.length === 0 ? (
        <div className={c.empty}>
          <p style={{ margin: '0 0 8px 0' }}>No props defined</p>
          <p style={{ margin: '0 0 16px 0', fontSize: '12px' }}>
            Props let users customize widget content
          </p>
          <Button onClick={handleAddProp} disabled={disabled} secondary>
            + Add First Prop
          </Button>
        </div>
      ) : (
        <>
          {schema.map((prop, index) => (
            <PropItemEditor
              key={index}
              prop={prop}
              index={index}
              depth={0}
              disabled={disabled}
              isFirst={index === 0}
              isLast={index === schema.length - 1}
              isHighlighted={highlightedIndex === index}
              onUpdate={(field, value) => handleUpdateProp(index, field, value)}
              onDelete={() => handleDeleteProp(index)}
              onUpdateChildren={(children) => handleUpdatePropChildren(index, children)}
              onMoveUp={() => handleMoveProp(index, index - 1)}
              onMoveDown={() => handleMoveProp(index, index + 1)}
            />
          ))}

          <Button
            className={c.addButton}
            onClick={handleAddProp}
            disabled={disabled}
            secondary
          >
            + Add Prop
          </Button>
        </>
      )}

      <div className={c.syntaxBox} style={{ marginTop: '16px', padding: '12px', borderRadius: '8px', fontSize: '12px' }}>
        <strong>Array Syntax in HTML:</strong>
        <pre className={c.syntaxCode} style={{ margin: '8px 0 0 0', padding: '8px', borderRadius: '4px', overflow: 'auto' }}>
{`{{#cards}}
  <div class="card">
    <h3>{{title}}</h3>
    <p>{{description}}</p>
  </div>
{{/cards}}`}
        </pre>
      </div>
    </div>
  );
};

export default PropsSchemaEditor;
